import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Card, CardHeader, CardBody, CardTitle, Row, Col,
  Button, Input, Label, Alert, Badge
} from "reactstrap";
import moment from "moment";
import { Enviroments } from "../../enviroments/enviroments";

/* ---------- Tema/cores ---------- */
const COLORS = {
  bg: "#0B0B0F",
  surface: "#121317",
  border: "#26272B",
  text: "#ECECF3",
  sub: "#A3A6AF",
  primary: "#913ef8",
  pink: "#e14eca",
  yellow: "#F6E05E",
  low: "#8B8E98",
};

/* ---------- Skeleton ---------- */
const Shimmer = ({ width = "100%", height = 18, radius = 6 }) => (
  <div
    style={{
      width,
      height,
      borderRadius: radius,
      background:
        "linear-gradient(90deg, rgba(255,255,255,0.06) 25%, rgba(255,255,255,0.12) 37%, rgba(255,255,255,0.06) 63%)",
      backgroundSize: "400% 100%",
      animation: "shimmer 1.2s ease-in-out infinite",
    }}
  />
);

const GlobalStyles = () => (
  <style>{`
    @keyframes shimmer {
      0% { background-position: 100% 0; }
      100% { background-position: -100% 0; }
    }
    .chip-scroll { overflow-x: auto; overflow-y: hidden; -webkit-overflow-scrolling: touch; }
  `}</style>
);

/* ---------- Badge por faixa (10+, 20+, 50+, 100+) ---------- */
const chipStyle = (odd) => {
  const v = Number(odd);
  if (Number.isNaN(v)) return { bg: COLORS.surface, text: COLORS.sub, border: COLORS.border };
  if (v >= 100) return { bg: "rgba(225,78,202,0.24)", text: COLORS.pink, border: COLORS.pink };
  if (v >= 50)  return { bg: "rgba(225,78,202,0.18)", text: COLORS.pink, border: COLORS.pink };
  if (v >= 20)  return { bg: "rgba(225,78,202,0.12)", text: COLORS.pink, border: COLORS.pink };
  if (v >= 10)  return { bg: "rgba(225,78,202,0.10)", text: COLORS.pink, border: COLORS.pink };
  return { bg: "#1b1c21", text: COLORS.text, border: COLORS.border };
};

function HistoryOddsPinks({ props, onResultsUpdated }) {
  const dateNow = useMemo(() => moment(new Date()).format("YYYY-MM-DD"), []);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [numberVelas, setNumberVelas] = useState(50);
  const [filterVelas, setFilterVelas] = useState(10); // mínimo para “rosa”
  const [errorMsg, setErrorMsg] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);

  /* ---------- Ordenação: mais recente -> mais antigo ---------- */
  const sortByNewest = useCallback((arr) => {
    return [...arr].sort((a, b) => {
      const A = a.date || `${dateNow} ${a.hour}`;
      const B = b.date || `${dateNow} ${b.hour}`;
      return new Date(B).getTime() - new Date(A).getTime();
    });
  }, [dateNow]);

  /* ---------- Fetch (sem depender de onResultsUpdated) ---------- */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const headers = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("apiToken") || ""}`,
        },
      };
      const betHouseCustom = props.betHouse === "BR4BET" ? "MINES_BET" : props.betHouse;
      const url = `${Enviroments.API_URL_NODE}history-filter-odd?date=${dateNow}&numberVelas=${numberVelas}&betHouse=${betHouseCustom}&filter=${filterVelas}`;
      const { data } = await axios.get(url, headers);

      const sorted = sortByNewest(data || []);
      setItems(sorted);
      setUpdatedAt(moment().format("HH:mm:ss"));
    } catch (err) {
      console.error(err);
      setErrorMsg("Não foi possível carregar agora. Tente novamente em instantes.");
    } finally {
      setLoading(false);
    }
  }, [dateNow, numberVelas, filterVelas, props?.betHouse, sortByNewest]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ---------- Notifica o pai quando o resultado visível muda ---------- */
  useEffect(() => {
    if (typeof onResultsUpdated === "function") {
      onResultsUpdated(items);
    }
  }, [items, onResultsUpdated]);

  return (
    <div className="history-odd">
      <GlobalStyles />

      <Row>
        <Col xs="12">
          <Card style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
            <CardHeader className="pb-2">
              <Row className="align-items-end">
                <Col md="6" className="mb-2 mb-md-0">
                  <h5 className="card-category" style={{ color: COLORS.sub }}>
                    Histórico de velas <span style={{ color: COLORS.pink, fontWeight: 700 }}>Rosas</span>
                  </h5>
                  <div className="d-flex align-items-center">
                    <CardTitle tag="h3" className="mb-0" style={{ color: COLORS.text }}>
                      Velas acima de {filterVelas}x
                    </CardTitle>
                    <Button
                      size="sm"
                      className="ml-3"
                      color="primary"
                      style={{ background: COLORS.primary, border: "none" }}
                      onClick={fetchData}
                      disabled={loading}
                    >
                      {loading ? "Atualizando..." : "Atualizar"}
                    </Button>
                  </div>

                  {updatedAt && (
                    <div className="mt-2" style={{ color: COLORS.sub, fontSize: 12 }}>
                      Última análise: <strong style={{ color: COLORS.text }}>{updatedAt}</strong>
                    </div>
                  )}
                </Col>

                <Col md="6" className="d-flex justify-content-md-end">
                  <div className="mr-3">
                    <Label htmlFor="selectVelas" style={{ color: COLORS.sub, fontSize: 12 }}>Qtd. de velas</Label>
                    <Input
                      id="selectVelas"
                      type="select"
                      value={numberVelas}
                      onChange={(e) => setNumberVelas(Number(e.target.value))}
                      style={{
                        width: 160,
                        background: COLORS.bg,
                        color: COLORS.text,
                        border: `1px solid ${COLORS.border}`,
                      }}
                    >
                      <option value="10">10</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                      <option value="200">200</option>
                      <option value="500">500</option>
                      <option value="1000">1000</option>
                    </Input>
                  </div>

                  <div>
                    <Label htmlFor="selectMin" style={{ color: COLORS.sub, fontSize: 12 }}>Valor mínimo (x)</Label>
                    <Input
                      id="selectMin"
                      type="select"
                      value={filterVelas}
                      onChange={(e) => setFilterVelas(Number(e.target.value))}
                      style={{
                        width: 160,
                        background: COLORS.bg,
                        color: COLORS.text,
                        border: `1px solid ${COLORS.border}`,
                      }}
                    >
                      <option value="10">10x</option>
                      <option value="20">20x</option>
                      <option value="30">30x</option>
                      <option value="50">50x</option>
                      <option value="100">100x</option>
                      <option value="500">500x</option>
                    </Input>
                  </div>
                </Col>
              </Row>
            </CardHeader>

            <CardBody>
              {errorMsg && (
                <Alert color="danger" style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
                  {errorMsg}
                </Alert>
              )}

              {/* Cabeçalho da lista */}
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span style={{ color: COLORS.sub, fontSize: 12 }}>
                  Resultados (mais recente →)
                </span>
                {!loading && (
                  <Badge pill style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, color: COLORS.sub }}>
                    {items.length} ocorrências
                  </Badge>
                )}
              </div>

              {/* Lista */}
              {loading ? (
                <div className="d-flex chip-scroll" style={{ gap: 8 }}>
                  {Array.from({ length: 12 }).map((_, i) => (
                    <Shimmer key={i} width={90} height={36} />
                  ))}
                </div>
              ) : items.length ? (
                <div className="chip-scroll" style={{ display: "flex", gap: 8, paddingBottom: 6 }}>
                  {items.map((item, idx) => {
                    const style = chipStyle(item.odd);
                    return (
                      <div
                        key={item.id ?? `${item.hour}-${idx}`}
                        title={`${item.hour} • ${Number(item.odd).toFixed(2)}x`}
                        style={{
                          padding: "6px 12px",
                          borderRadius: 14,
                          border: `1px solid ${style.border}`,
                          background: style.bg,
                          color: style.text,
                          fontWeight: 800,
                          minWidth: 72,
                          textAlign: "center",
                          whiteSpace: "nowrap",
                          fontSize: 14,
                        }}
                      >
                        {Number(item.odd).toFixed(2)}x
                        <div style={{ fontSize: 11, color: COLORS.sub }}>{item.hour}</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ color: COLORS.sub, fontSize: 13 }}>
                  Nenhum resultado ≥ {filterVelas}x no período selecionado.
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default HistoryOddsPinks;
